import axios from 'axios';
import { getAuthHeader } from './authService';

const API_URL = `${process.env.REACT_APP_SERVER_URL}/api/contracts`;

const headers = () => getAuthHeader();

export type ContractTemplate = {
  id: number;
  name: string;
  body: string;
  publication_id: number | null;
  createdAt?: string;
  updatedAt?: string;
  publication?: { id: number; name: string };
};

export type Contract = {
  id: number;
  template_id: number;
  magazine_booking_id: number | null;
  proposal_id: number | null;
  company_id: number | null;
  sales_rep_id: number;
  status: 'draft' | 'sent' | 'signed';
  body: string;
  terms_and_conditions?: string | null;
  sent_at: string | null;
  signed_at: string | null;
  signer_name?: string | null;
  signer_email?: string | null;
  signed_ip?: string | null;
  signed_user_agent?: string | null;
  document_hash?: string | null;
  sign_token_expires_at?: string | null;
  signature_email_text?: string | null;
  signature_email_cc?: string | null;
  default_signature_email?: string;
  email_text?: string | null;
  createdAt: string;
  updatedAt: string;
  template?: { id: number; name: string; publication_id?: number | null };
  company?: { id: number; name: string };
  sales_rep?: { id: number; username: string; email: string };
  proposal?: {
    id: number;
    status: string;
    contact_name?: string | null;
    contact_email?: string | null;
    contract_from?: number;
    contract_from_company?: { id: number; name: string } | null;
    terms_and_conditions?: string | null;
    line_items?: {
      id: number;
      label: string;
      size?: string | null;
      price?: string | number;
      ad_slot?: {
        label: string;
        issue?: {
          title: string;
          publication?: { name: string };
        };
      };
    }[];
  };
  magazine_booking?: {
    id: number;
    status: string;
    ad_slot?: {
      label: string;
      issue?: {
        title: string;
        publication?: { name: string };
      };
    };
  };
};

export const TEMPLATE_PLACEHOLDERS =
  '{{contract_from}}, {{company_name}}, {{company_logo}}, {{contact_name}}, {{contact_email}}, {{publication}}, {{publications}}, {{issue}}, {{ad_slot}}, {{ad_size}}, {{price}}, {{line_items_table}}, {{total_price}}, {{proposal_notes}}, {{proposal_status}}, {{sales_rep}}, {{booking_status}}, {{booking_notes}}, {{signer_name}}, {{signed_date}}, {{date}}';

export const getContractTemplates = async () => {
  try {
    const response = await axios.get(`${API_URL}/templates`, { headers: headers() });
    return response.data as ContractTemplate[];
  } catch (error: any) {
    console.log(error.message);
    return [];
  }
};

export const createContractTemplate = async (data: {
  name: string;
  body: string;
  publication_id?: number | null;
}) => {
  const response = await axios.post(`${API_URL}/templates`, data, { headers: headers() });
  return response.data as ContractTemplate;
};

export const updateContractTemplate = async (
  id: number,
  data: { name: string; body: string; publication_id?: number | null }
) => {
  const response = await axios.put(`${API_URL}/templates/${id}`, data, { headers: headers() });
  return response.data as ContractTemplate;
};

export const deleteContractTemplate = async (id: number) => {
  await axios.delete(`${API_URL}/templates/${id}`, { headers: headers() });
};

export const getContracts = async (status?: string[]) => {
  try {
    const params = status ? { status: status } : {};
    const response = await axios.get(API_URL, { headers: headers(), params });
    return response.data as Contract[];
  } catch (error: any) {
    console.log(error.message);
    return [];
  }
};

export const getContract = async (id: string) => {
  const response = await axios.get(`${API_URL}/${id}`, { headers: headers() });
  return response.data as Contract;
};

export type DealActivity = {
  id: number;
  action: string;
  summary: string;
  metadata?: Record<string, unknown>;
  createdAt: string;
  user?: { id: number; username: string; email: string } | null;
};

export const getContractActivity = async (contractId: string | number): Promise<DealActivity[]> => {
  try {
    const response = await axios.get(`${API_URL}/${contractId}/activity`, { headers: headers() });
    return response.data;
  } catch (error: any) {
    console.log(error.message);
    return [];
  }
};

export const generateContractFromProposal = async (proposalId: number, templateId?: number) => {
  const response = await axios.post(
    `${API_URL}/generate`,
    { proposal_id: proposalId, ...(templateId ? { template_id: templateId } : {}) },
    { headers: headers() }
  );
  return response.data as Contract;
};

export const generateContract = async (magazineBookingId: number, templateId: number) => {
  const response = await axios.post(
    `${API_URL}/generate`,
    { magazine_booking_id: magazineBookingId, template_id: templateId },
    { headers: headers() }
  );
  return response.data as Contract;
};

export const updateContractStatus = async (id: number, status: string) => {
  const response = await axios.patch(`${API_URL}/${id}/status`, { status }, { headers: headers() });
  return response.data as Contract;
};

export const sendContractForSignature = async (id: number, resend: boolean, recipientEmail?: string, ccEmail?: string) => {
  const response = await axios.post(
    `${API_URL}/${id}/send-for-signature`,
    { resend, ...(recipientEmail ? { recipient_email: recipientEmail } : {}), ...(ccEmail ? { cc_email: ccEmail } : {}) },
    { headers: headers() }
  );
  return response.data as Contract & { sign_url?: string };
};

export const saveSignatureEmail = async (contractId: number, emailText: string, emailSubject: string, ccEmail?: string) => {
  const response = await axios.post(
    `${API_URL}/save_signature_email`,
    { contract_id: contractId, email_text: emailText, subject: emailSubject, cc_email: ccEmail || null },
    { headers: headers() }
  );
  return response.data as Contract;
};

export const deleteContract = async (id: number) => {
  await axios.delete(`${API_URL}/${id}`, { headers: headers() });
};
